import React, { useEffect, useState, useMemo, useId } from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const polar = (cx, cy, r, angleDeg) => {
	const a = (angleDeg * Math.PI) / 180.0;
	return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const Odometer = ({ value = 0, digits = 6, height = 28, small = false }) => {
	const str = useMemo(() => {
		const n = Math.max(0, Math.floor(Number(value) || 0));
		return String(n).padStart(digits, "0");
	}, [value, digits]);

	return (
		<Box
			sx={{
				display: "inline-grid",
				gridAutoFlow: "column",
				alignItems: "center",
				gap: 0.75,
				px: 1,
				py: 0.5,
				borderRadius: 1,
				bgcolor: "#111",
				boxShadow:
					"inset 0 -2px 0 rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.35)",
				outline: "1px solid rgba(255,255,255,0.08)",
			}}
		>
			{Array.from(str).map((ch, i) => {
				const isLast = i === str.length - 1;
				return (
					<Box
						key={`${i}-${ch}`}
						sx={{
							width: small ? 18 : 22,
							height,
							display: "grid",
							placeItems: "center",
							borderRadius: 0.5,
							bgcolor: isLast ? "#eaeaea" : "#1b1b1b",
							color: isLast ? "#111" : "#eaeaea",
							fontFamily:
								"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
							fontWeight: 700,
							fontSize: small ? 14 : 16,
							lineHeight: 1,
							textShadow: isLast
								? "none"
								: "0 1px 0 rgba(0,0,0,0.6)",
							boxShadow: isLast
								? "inset 0 1px 0 rgba(0,0,0,0.1), inset 0 -2px 0 rgba(0,0,0,0.25)"
								: "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -2px 0 rgba(0,0,0,0.6)",
							border: "1px solid rgba(255,255,255,0.06)",
							position: "relative",
							"&::after": {
								content: '""',
								position: "absolute",
								left: 0,
								right: 0,
								top: "50%",
								height: 1,
								background: "rgba(255,255,255,0.06)",
								transform: "translateY(-0.5px)",
							},
						}}
					>
						{ch}
					</Box>
				);
			})}
			<Typography
				variant="caption"
				sx={{
					ml: 0.75,
					color: "#bdbdbd",
					letterSpacing: 1,
					fontFamily:
						"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
					userSelect: "none",
				}}
			>
				KM
			</Typography>
		</Box>
	);
};

const MainGauge = ({ value, max, odometer }) => {
	const [size, setSize] = useState({
		width: 340,
		height: 180,
		smallScreens: false,
	});
	const safeId = (p) => String(p).replace(/[^a-zA-Z0-9_-]/g, "-");
	const uid = safeId(useId());
	const gradId = `mg-grad-${uid}`;
	const needleGradId = `mg-needle-${uid}`;
	const glowId = `mg-glow-${uid}`;

	useEffect(() => {
		const mq = window.matchMedia("(max-width: 800px)");
		const handle = (e) => {
			setSize(
				e.matches
					? { width: 240, height: 130, smallScreens: true }
					: { width: 340, height: 200, smallScreens: false }
			);
		};
		handle(mq);
		mq.addEventListener("change", handle);
		return () => mq.removeEventListener("change", handle);
	}, []);

	const vNum = Number(value) || 0;
	const maxNum = Number(max) > 0 ? Number(max) : 60;
	const v = Math.min(Math.max(0, vNum), maxNum);
	const angle = (v / maxNum) * 180 - 90;

	const cx = size.width / 2;
	const cy = size.height - 10;
	const r = size.width / 2 - 30;
	const needleLen = r - 8;

	const steps = 8;
	const ticks = [];
	for (let i = 0; i <= steps; i++) {
		const deg = -180 + i * (180 / steps);
		const outer = polar(cx, cy, r + 10, deg);
		const inner = polar(cx, cy, r - 8, deg);
		ticks.push({
			deg,
			outer,
			inner,
			label: Math.round((i * maxNum) / steps),
		});
	}

	return (
		<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
			<Box sx={{ position: "relative" }}>
				<svg height={size.height} width={size.width}>
					<defs>
						<linearGradient
							id={gradId}
							x1="0%"
							y1="0%"
							x2="100%"
							y2="0%"
						>
							<stop offset="0%" stopColor="#00c853" />
							<stop offset="60%" stopColor="#ffd600" />
							<stop offset="100%" stopColor="#ff3d00" />
						</linearGradient>
						<linearGradient
							id={needleGradId}
							x1="0"
							y1="0"
							x2="0"
							y2="1"
						>
							<stop offset="0%" stopColor="#ff8a65" />
							<stop offset="100%" stopColor="#ff3d00" />
						</linearGradient>
						<filter id={glowId}>
							<feDropShadow
								dx="0"
								dy="0"
								stdDeviation="2"
								floodColor="rgba(255,69,0,0.7)"
							/>
						</filter>
					</defs>

					{/* base arc */}
					<path
						d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${
							cx + r
						} ${cy}`}
						fill="none"
						stroke="#222"
						strokeWidth="24"
					/>

					{/* progress arc */}
					<path
						d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${
							cx + r
						} ${cy}`}
						fill="none"
						stroke={`url(#${gradId})`}
						strokeWidth={size.smallScreens ? "15" : "24"}
						strokeDasharray={`${(v / maxNum) * Math.PI * r} ${
							Math.PI * r
						}`}
						style={{ transition: "stroke-dasharray 0.6s ease" }}
					/>

					{/* ticks */}
					{ticks.map((t, i) => (
						<g key={i}>
							<line
								x1={t.inner.x}
								y1={t.inner.y}
								x2={t.outer.x}
								y2={t.outer.y}
								stroke="#444"
								strokeWidth={i % 2 === 0 ? 3 : 1.5}
							/>
							<text
								x={polar(cx, cy, r - 28, t.deg).x}
								y={polar(cx, cy, r - 28, t.deg).y + 5}
								textAnchor="middle"
								fontSize={size.smallScreens ? "8" : "10"}
								fill="black"
							>
								{t.label}
							</text>
						</g>
					))}

					{/* needle + hub */}
					<motion.g
						initial={{ transform: `rotate(-90 ${cx} ${cy})` }}
						animate={{ transform: `rotate(${angle} ${cx} ${cy})` }}
						transition={{
							type: "spring",
							stiffness: 120,
							damping: 14,
						}}
					>
						<g transform={`rotate(${angle} ${cx} ${cy})`}>
							<line
								x1={cx}
								y1={cy}
								x2={cx}
								y2={cy - needleLen}
								stroke="#ff3d00"
								strokeWidth="4"
								strokeLinecap="round"
								/* remove filter while debugging; re-add later */
								// filter={`url(#${glowId})`}
							/>
							<circle
								cx={cx}
								cy={cy}
								r="9"
								fill="#fff"
								stroke="#000"
								strokeWidth="4"
							/>
						</g>
					</motion.g>
				</svg>

				{/* speed label */}
				<Box
					sx={{
						position: "absolute",
						width: size.width,
						left: 0,
						top: cy - r * 0.55,
						textAlign: "center",
						pointerEvents: "none",
					}}
				>
					<Typography
						variant={size.smallScreens ? "h6" : "h4"}
						sx={{ color: "black", fontWeight: 800, lineHeight: 1 }}
					>
						{Math.round(vNum)}{" "}
						<span style={{ fontSize: 14 }}>KM/H</span>
					</Typography>
				</Box>

				{/* odometer below */}
				<Box
					sx={{
						mt: size.smallScreens ? 0.5 : 1,
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Odometer
						value={odometer}
						digits={6}
						height={size.smallScreens ? 22 : 28}
						small={size.smallScreens}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default MainGauge;
